import React, { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import QuizTypes from 'types/QuizTypes';
import TimerTypes from 'types/TimerTypes';
import Statuses from 'types/Statuses';
import ConsentsStatuses from 'types/ConsentsStatuses';
import PageTypes from 'types/PageTypes';
import QuestionTypes from 'types/QuestionTypes';
// Models
import { RootState } from 'store';
import IQuiz from 'models/Quiz';
import IBucket from 'models/Bucket';
import IScoringResultBand from 'models/ScoringResultBand';
import IQuestion from 'models/Question';
import IAnswer from 'models/Answer';
import ITimer from 'models/Timer';
import IPage from 'models/Page';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import { getQuiz, updateQuiz } from 'store/quiz/quizzesAsync';
import ConsentsAsync from 'store/consents/consentsAsync';
// Actions
import { updateForm, deleteForm, setHash } from 'store/form/formSlice';
import { setInitialField } from 'store/quiz/quizzesSlice';
import { appActions } from 'store/app/appSlice';
import { consentsActions } from 'store/consents/consentsSlice';
import { setInitialField as setQuizEventInitialField } from 'store/quizEvent/quizEventSlice';
// Selectors
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import { selectFormByQuizId } from 'store/form/formSelectors';
import { getNamespaceItems } from 'store/namespace/namespaceSelectors';
import { getUpdateCorrectAnswersLoading } from 'store/result/resultSelectors';
import { selectConsents } from 'store/consents/consentsSelectors';
// Initial form
import { initialQuizForm } from 'store/form/formInitial';
// Components
import { Button } from 'components/Buttons';
import QuizFormSidebar from 'components/QuizFormSidebar';
import { QuizToolbar } from 'components/QuizToolbar';
import TabContentQuiz from './TabContentQuiz';
import TabContentPages from './TabContentPages';
import TabContentPostQuizPages from './TabContentPostQuizPages';
import TabContentCorrectAnswers from './TabContentCorrectAnswers';
import UpdateCorrectAnswerLoader from './UpdateCorrectAnswerLoader';
import TabContentLiveLeaderboard from './TabContentLiveLeaderboard';
import Tabs from 'components/QuizFormSidebar/Tabs';
import QuizMenu from 'components/QuizMenu';
// Utilties
import { encodeBase64 } from 'utilities/utilities';

const QuizFormPage:React.FC = () => {
  const { quizId } = useParams<{ quizId:string }>();
  const { search } = useLocation<{ search:string }>();

  const params = new URLSearchParams(search);
  const activeTab = params.get('tab') || 'quiz';

  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz | null = useAppSelector(selectQuiz);
  const quizForm:any = useAppSelector((state:RootState) => selectFormByQuizId(state, { quizId }));
  const namespaces = useAppSelector(getNamespaceItems);
  const isLoading = useAppSelector(getUpdateCorrectAnswersLoading);
  const consents = useAppSelector(selectConsents);

  const timeout = useRef<any>(null);
  const isMounted = useRef(false);

  const methods = useForm<any>({
    defaultValues: initialQuizForm
  });

  const handleClearTimeout = () => {
    if ( timeout.current ) clearTimeout(timeout.current);
  }

  const getNamespaceSlug = (namespaceId:string | undefined) => {
    if ( !namespaceId ) return null;
    const foundedNamespace = namespaces?.find((namespace:any) => namespace._id === namespaceId);
    if ( !foundedNamespace ) return null;
    return foundedNamespace.slug;
  }

  const languageWatcher = methods.watch('language');

  useEffect(() => {
    dispatch(getQuiz(quizId));
    // eslint-disable-next-line
    dispatch(ConsentsAsync.getConsents({ status: ConsentsStatuses.published, ['include.platform']: true }));

    return () => {
      isMounted.current = false;

      handleClearTimeout();
      dispatch(setInitialField('quiz'));
      dispatch(setHash(null));
      dispatch(consentsActions.setInitialField('consents'));
      dispatch(setQuizEventInitialField('quizEvents'));
    }
    // eslint-disable-next-line
  }, []);
    
  useEffect(() => {
    if (!consents) return;

    const filteredConsents = consents.filter(consent => consent.language === languageWatcher);
    const quizConsents = quiz?.consents?.filter(consentId => filteredConsents.find(consent => consent._id === consentId)) || [];
    const quizConsentsLinks = quiz?.consentsLinks?.filter(consentId => filteredConsents.find(consent => consent._id === consentId)) || [];
    const requiredConsentsIds = filteredConsents.filter(consent => (consent.language === languageWatcher && consent.required)).map(c => c._id);
    
    const set = new Set([...quizConsents, ...requiredConsentsIds]);


    methods.setValue('consents', [...set]);
    methods.setValue('consentsLinks', quizConsentsLinks);
    // eslint-disable-next-line
  }, [languageWatcher, consents]);

  useEffect(() => {
    if ( quiz ){
      isMounted.current = true;

      const quizData:any = {...initialQuizForm, ...quiz };

      const nextQuizData = {...quizData, nonce: uuidv4(), clientNameSlug: getNamespaceSlug(quizData.namespace._id) };
      
      if ( !quizForm ){
        dispatch(updateForm({ quizId, quizForm: nextQuizData }));
        dispatch(setHash(encodeBase64(nextQuizData)));
      } else {
        dispatch(setHash(encodeBase64(nextQuizData)));
      }

      const quizFormData = {
        ...nextQuizData,
        registrationPage: nextQuizData.registrationPage || null,
        quizGroup: quiz.quizGroup ? quiz.quizGroup._id : '',
        startsDate: nextQuizData.startsDate ? new Date(Number(nextQuizData.startsDate)) : '',
        endsDate: nextQuizData.endsDate ? new Date(Number(nextQuizData.endsDate)) : '',
        spotPrizes: quiz.spotPrizes ? quiz.spotPrizes.join('') : '',
        namespaceId: nextQuizData.namespaceId || quiz.namespace._id,
        prizes: quiz.prizes ? quiz.prizes.map((prize:string) => ({ prize })) : [],
        integrations: quiz.integrations ? quiz.integrations.map(integration => ({ integrationId: integration._id, value: true })) : [],
        timers: nextQuizData.timers ? nextQuizData.timers.map((timer: ITimer) => timer.type === TimerTypes.Absolute ? ({ ...timer, time: new Date(Number(timer.time)) }) : timer ) : [],
        callToAction: nextQuizData.callToAction ? { ...initialQuizForm.callToAction, ...nextQuizData.callToAction } : nextQuizData.callToAction,
        scoringResultPage: nextQuizData.scoringResultPage ? { ...initialQuizForm.scoringResultPage, ...nextQuizData.scoringResultPage } : nextQuizData.scoringResultPage,
        publicLeaderboard: nextQuizData.publicLeaderboard ? { ...initialQuizForm.publicLeaderboard, ...nextQuizData.publicLeaderboard } : nextQuizData.publicLeaderboard,
        questions: nextQuizData.questions?.map((question:IQuestion) => {
          if (question.type === QuestionTypes.TopList) {
            return ({
              ...question,
              score: question.score ?? '',
              config: {
                ...(question.config || {}),
                items: question.config.items?.map((item) => ({
                  ...item,
                  correctAnswers: item.correctAnswers.map((answer:string) => ({ title: answer })),
                }))
              }
            })
          }
          if (question.type === QuestionTypes.ShortText && question.config?.correctAnswers) {
            return ({
              ...question,
              score: question.score ?? '',
              config: {
                ...question.config,
                correctAnswers: question.config.correctAnswers.map((answer:string) => ({ title: answer })),
              }
            })
          }
          return { ...question, score: question.score ?? '' };
        })
      };
      methods.reset(quizFormData);
    }
    // eslint-disable-next-line
  }, [quiz]);

  useEffect(() => {
    const subscription = methods.watch((value, { name, type }) => {
      if ( type === 'change' ){
        handleClearTimeout();
        timeout.current = setTimeout(() => {
          dispatch(setHash(null));
          const quizForm:IQuiz = {
            ...value,
            spotPrizes: value.spotPrizes ? [value.spotPrizes] : [],
            prizes: value.prizes ? value.prizes.map((prize:any) => prize.prize) : [],
            startsDate: value.startsDate ? moment(value.startsDate).format('x') : '',
            endsDate: value.endsDate ? moment(value.endsDate).format('x') : '',
            namespace: value.namespaceId,
            nonce: uuidv4(),
            clientNameSlug: getNamespaceSlug(value.namespace),
            registrationPage: value.registrationPage || null,
            questions: value.questions?.map((question:IQuestion) => {
              if (question.type === QuestionTypes.TopList) {
                return ({
                  ...question,
                  config: {
                    ...(question.config || {}),
                    items: question.config.items?.map((item) => ({
                      ...item,
                      correctAnswers: item.correctAnswers.map((answer:any) => answer.title),
                    }))
                  }
                })
              }
              if (question.type === QuestionTypes.ShortText && question.config?.correctAnswers) {
                return ({
                  ...question,
                  config: {
                    ...question.config,
                    correctAnswers: question.config.correctAnswers.map((answer:any) => answer.title),
                  }
                })
              }
              return question;
            })
          };
          dispatch(updateForm({ quizId, quizForm }));
          dispatch(setHash(encodeBase64(quizForm)));
        }, 1000);
      }
    });
    return () => {
      if ( timeout.current ) clearTimeout(timeout.current);
      subscription.unsubscribe();
    }
    // eslint-disable-next-line
  }, [methods.watch]);

  const onSubmit = methods.handleSubmit((data:any) => {
    let questions = data.questions.map((question:IQuestion) => {
      const { format, score, timeout, ...otherQuestionData } = question;
      const nextQuestion:any = {
        ...otherQuestionData,
        ...(format && { format }),
        ...(score && { score: Number(score) }),
        timeout: timeout ? Number(timeout) : 0
      }
      if (data.type === QuizTypes.Streak) {
        nextQuestion.config = {
          ...(nextQuestion.config || {}),
          choices: question.answers
        }
      }
      if (nextQuestion.config?.referenceAnswer) {
        nextQuestion.config = {
          ...(nextQuestion.config || {}),
          referenceAnswer: {
            ...nextQuestion.config.referenceAnswer,
            score: Number(nextQuestion.config.referenceAnswer.score)
          },
          choices: question.answers
        }
      }
      if (question.type === QuestionTypes.Ranking) {
        nextQuestion.config = {
          ...(nextQuestion.config || {}),
          choices: question.answers
        }
      }
      if (question.type === QuestionTypes.TopList) {
        nextQuestion.config = {
          ...(nextQuestion.config || {}),
          items: nextQuestion.config.items?.map((item:any) => ({
            ...item,
            correctAnswers: item.correctAnswers.map((answer:any) => answer.title),
            scoring: { points: Number(item.scoring?.points) || 1000 }
          }))
        }
      }
      if (question.type === QuestionTypes.ShortText && question.config?.correctAnswers) {
        nextQuestion.config = {
          ...nextQuestion.config,
          correctAnswers: question.config.correctAnswers.map((answer:any) => answer.title)
        }
      }
      return nextQuestion;
    });

    if ( data.type === QuizTypes.Personality ){
      const bucketIds:string[] = data.buckets.map((bucket:IBucket) => bucket.ref);
      questions = data.questions.map((question:IQuestion) => ({
        ...question,
        score: undefined,
        answers: question.answers.map((answer:IAnswer) => ({
          ...answer,
          bucketScores: answer.bucketScores
            ? answer.bucketScores.filter((bucketScore:any) => bucketIds.includes(bucketScore.ref)).map((bucketScore:any) => ({
              ...bucketScore,
              score: Number(bucketScore.score)
            }))
            : []
        }))
      }));
    };

    const { namespaceId, translations, ...otherData } = data;

    const nextTranslations:any = {};

    Object.keys(translations).forEach(key => {
      if (data.additionalLanguages?.includes(key)) nextTranslations[key] = translations[key];
    })

    const newData:any = {
      ...otherData,
      publicLeaderboard: otherData.publicLeaderboard ? {
        ...otherData.publicLeaderboard,
        questionsTimeout: otherData.publicLeaderboard.questionsTimeout ? Number(otherData.publicLeaderboard.questionsTimeout) : 2,
      } : otherData.publicLeaderboard,
      translations: nextTranslations,
      registrationPage: otherData.registrationPage || null,
      questions,
      questionsSettings: {
        ...(otherData.questionsSettings || {}),
        delay: otherData.questionsSettings?.delay ? Number(otherData.questionsSettings.delay) : 2,
        limit: otherData.questionsSettings?.limit ? Number(otherData.questionsSettings.limit) : undefined,
      },
      callToAction: {
        ...(otherData.callToAction || {}),
        clickBonusPoints: data.callToAction?.clickBonusPoints ? Number(data.callToAction.clickBonusPoints) : 0,
      },
      scoringResultPage: otherData.scoringResultPage ? {
        ...otherData.scoringResultPage,
        bands: otherData.scoringResultPage.bands?.map((band:IScoringResultBand) => ({
          ...band,
          minPercentage: Number(band.minPercentage),
        })),
      } : otherData.scoringResultPage,
      namespace: namespaceId,
      integrations: data.integrations.reduce((acc:string[], cur:any) => {
        if (cur.value) acc.push(cur.integrationId);
        return acc;
      }, []),
    };
    if ( data.startsDate ) newData['startsDate'] = moment(data.startsDate).format('x');
    if ( data.endsDate ) newData['endsDate'] = moment(data.endsDate).format('x');
    if ( data.timers ) newData['timers'] = data.timers.map((timer:ITimer) => {
      if (timer.type === TimerTypes.Absolute) return ({
        ...timer,
        time: moment(timer.time).format('x')
      })
      return timer;
    });
    if ( data.banners ) newData['banners'] = data.banners.map((banner:any) => ({
      ...banner,
      timeout: banner.timeout ? Number(banner.timeout) : undefined,
    }));
    if ( [QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.BeatTheExpert, QuizTypes.Streak, QuizTypes.TeamSelector].includes(data.type) ){
      newData['spotPrizes'] = data.spotPrizes ? [data.spotPrizes] : [];
      newData['prizes'] = data.prizes ? data.prizes.map((prize:any) => prize.prize) : [];
    }
    const filteredData:any = {};
    Object.keys(newData).forEach((key) => {
      const unnecessaryKeys = ['account', 'updatedBy', 'createdAt', 'updatedAt', '__v', '_id'];
      if (unnecessaryKeys.includes(key)) return;
      // if (newData[key] === null) return;
      // if (typeof newData[key] === 'string' && !newData[key]) return;
      // if (Array.isArray(newData[key]) && !newData[key].length) return;

      filteredData[key] = newData[key];
    })
    updateQuizAsync({ quizId, data: filteredData });

    if (quiz?.type === QuizTypes.RankingPoll) {
      const questionsLength = newData.pages?.filter((page:IPage) => page.type === PageTypes.Question).length || 0;
      if (questionsLength > newData.questionsSettings?.answersOptions?.length) {
        dispatch(appActions.createNotification({ message: t('validations.questionsMoreThanAnswers'), status: Statuses.Warning }));
      }
    }
  });

  const updateQuizAsync = async (nextData:any)  => {
    try {
      await dispatch(updateQuiz(nextData)).unwrap();
      dispatch(deleteForm(quizId));
    } catch(err){}
  }

  const handleCancel = () => {
    dispatch(deleteForm(quizId));
    dispatch(getQuiz(quizId));
  };

  const isQuizContentActive = !activeTab || activeTab === 'quiz';
  const isPagesContentActive = activeTab === 'pages';
  const isPostQuizPagesContentActive = activeTab === 'postQuizPages';
  const isCorrectAnswersContentActive = activeTab === 'correctAnswers';
  const isLiveLeaderboardContentActive = activeTab === 'liveLeaderboard';

  if ( !quiz ) return null;
  if ( !isMounted.current ) return null;
  return (
    <FormProvider {...methods}>
      <form
        className="
          flex flex-col container w-full h-full max-w-screen-desktop
          overflow-x-hidden
          overflow-y-auto tablet:overflow-y-hidden
        "
        onSubmit={onSubmit}
        noValidate
      >
        <div className="">
          <div className="
            flex
            flex-col tablet:flex-row
            items-start: tablet:items-center
            pt-4
          ">
            <div className="flex-grow flex gap-2">
              <QuizToolbar />
              <div className="lg:-mt-1.5">
                <QuizMenu quiz={quiz} isInternal />
              </div>
            </div>
            <div>
              <Button
                className="mr-4"
                variant="outlined"
                onClick={handleCancel}
              >{t('pages.quizForm.clearChangesButton')}</Button>
              <Button
                type="submit"
                variant="contained"
                loading={isLoading}
              >{t('buttons.save')}</Button>
            </div>
          </div>
          <Tabs activeTab={activeTab} />
        </div>
        {isLoading ? <UpdateCorrectAnswerLoader /> : (
          <div className="
            overflow-y-visible tablet:overflow-y-auto
            block tablet:flex
            flex-col tablet:flex-row tablet:gap-6
            mb-4
            flex-grow
          ">
            <QuizFormSidebar size={isQuizContentActive ? 'small' : 'default'} />
            {isQuizContentActive ? <TabContentQuiz /> : null}
            {isLiveLeaderboardContentActive ? <TabContentLiveLeaderboard /> : null}
            {isPagesContentActive ? <TabContentPages /> : null}
            {isPostQuizPagesContentActive ? <TabContentPostQuizPages /> : null}
            {isCorrectAnswersContentActive ? <TabContentCorrectAnswers /> : null}
          </div>
        )}
      </form>
    </FormProvider>
  )
}

export default QuizFormPage;
